/**
 * Every string the comment moderation queue renders.
 *
 * The notice at the top states **what the setting does**, not what the code
 * does: comment moderation runs before publication because that is the
 * configured value, and flipping it turns this queue from a gate into a review
 * after the fact with no development. The copy addresses the operator — it never
 * names the setting itself, which is technical vocabulary the screen has no use
 * for.
 */
export const copy = {
  crumb: 'التعليقات',

  head: {
    title: 'التعليقات',
    lede: 'كلّ تعليق يمرّ من هنا قبل أن يظهر. اعتماده ينشره في مكانه على صفحة موضوعه، ورفضه يُبقيه مرئياً لكاتبه وحده.',
  },

  notice: {
    title: 'التعليقات تُراجَع قبل نشرها',
    body: 'لا يظهر التعليق لأحد غير كاتبه حتى يُعتمد. الكاتب يراه في مكانه بحالته المحجوزة، ويصله إشعار «اعتُمد تعليقك» عند الاعتماد.',
  },

  section: {
    heading: 'بانتظار المراجعة',
  },

  /** «تُعرَض منها ٥ في هذه الصفحة.» — the pill carries the whole depth. */
  truncation: {
    prefix: 'تُعرَض منها',
    suffix: 'في هذه الصفحة.',
  },

  /**
   * The traversal control. `backend-engagement` pages this queue by offset and
   * returns the total it drew from, so the pager states the page of how many and
   * the cards of how many. The pill above still carries `meta.total_held`, which
   * is the queue's whole depth and not this page's share of it — two figures
   * that answer two questions and are never folded into one.
   */
  pager: {
    label: 'صفحات طابور التعليقات',
    shownOne: 'تعليق واحد معروض',
    shownTwo: 'تعليقان معروضان',
    shownFew: 'تعليقات معروضة',
    shownMany: 'تعليقاً معروضاً',
  },

  /** «على <العنوان> · فيلم» — where the words land, read before they are read. */
  subjectPrefix: 'على',

  held: 'محجوز',

  actions: {
    approve: 'اعتمد',
    reject: 'ارفض',
  },

  /** The comment a held reply answers — context, never a second work item. */
  parentLabel: 'ردّاً على',

  approved: 'اعتُمد التعليق ونُشر في مكانه.',
  /** The cascade the server reported, stated rather than guessed at. */
  rejected: (removed: string) => `رُفض التعليق. حُجب معه ${removed} من الردود المنشورة.`,
  rejectedAlone: 'رُفض التعليق.',

  dialog: {
    heading: (author: string) => `رفض تعليق «${author}»؟`,
    body: 'يبقى التعليق مرئياً لكاتبه في مكانه ولا يظهر لأحد غيره. لا يصله إشعار اعتماد، ولا يُحذف من سجلّه. وتُرفَض معه كلّ الردود المعلّقة عليه.',
    requiredNote: 'الحقول المعلَّمة بـ* مطلوبة.',
    reasonLabel: 'سبب الرفض',
    reasonHint:
      'يُحفَظ في سجلّ القرار ولا يظهر لكاتب التعليق. قرارٌ نهائيّ بلا سبب مكتوب لا يستطيع أحد مراجعته لاحقاً.',
    commit: 'ارفض التعليق',
  },

  states: {
    loading: 'جارٍ تحميل التعليقات',
    emptyTitle: 'لا تعليق ينتظر المراجعة',
    emptyBody: 'عولج كلّ ما وصل. التعليق الجديد يظهر هنا فور إرساله، ولا يظهر لأحد غير كاتبه قبل أن يُعتمد.',
    emptyAction: 'عُد إلى لوحة التشغيل',

    /**
     * A page that emptied under the operator's own decisions — not a queue that
     * is done. The two never share a sentence: the second would report finished
     * work that is still waiting on the pages before this one.
     */
    emptyPageTitle: 'لم يبقَ شيء في هذه الصفحة',
    emptyPageBody: 'عولج كلّ ما كان معروضاً هنا. ما تبقّى من الطابور في الصفحات قبلها.',
    emptyPageAction: 'عُد إلى الصفحة السابقة',
  },
} as const;
